import React from "react";
import { useTranslate } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Timeline, Typography, theme } from "antd";
import { GlassCard } from "../../../../shared/components";
import {
    CloseCircleOutlined,
    SyncOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    ClockCircleOutlined,
} from "@ant-design/icons";

interface OrderHistoryTimelineProps {
    order: any;
}

export const OrderHistoryTimeline: React.FC<OrderHistoryTimelineProps> = ({ order }) => {
    const _t = useTranslate();
    const { token } = theme.useToken();

    const getIcon = (status: string) => {
        const style = { fontSize: "1.5em" };
        switch (status) {
            case "ongoing":
                return <SyncOutlined spin style={{ ...style, color: token.colorPrimary }} />;
            case "completed":
                return <CheckCircleOutlined style={{ ...style, color: token.colorSuccess }} />;
            case "sav":
                return <WarningOutlined style={{ ...style, color: token.colorWarning }} />;
            case "canceled":
                return <CloseCircleOutlined style={{ ...style, color: token.colorError }} />;
            case "submitted":
            default:
                return <ClockCircleOutlined style={style} />;
        }
    };

    const getStatusTitle = (previousStatus: string, newStatus: string) => {
        switch (newStatus) {
            case "submitted":
                return _t("orders.history.statusChanges.submitted");
            case "ongoing":
                return _t("orders.history.statusChanges.ongoing");
            case "completed":
                return _t("orders.history.statusChanges.completed");
            case "sav":
                return _t("orders.history.statusChanges.sav");
            case "canceled":
                return _t("orders.history.statusChanges.canceled");
            default:
                return _t("orders.history.statusChanges.statusChanged", {
                    previousStatus,
                    newStatus,
                });
        }
    };

    const orderHistory = order?.history || [];
    const sortedHistory = [...orderHistory].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    return (
        <GlassCard className="shadow-lg pl-9 pr-10">
            <Typography.Title level={5} style={{ margin: 0, fontWeight: "bold" }}>
                {_t("orders.titles.history")}
            </Typography.Title>
            {sortedHistory.length > 0 ? (
                <Timeline
                    mode="left"
                    className="mt-12"
                    items={sortedHistory.map((item) => ({
                        key: item.id,
                        dot: getIcon(item.newStatus),
                        children: (
                            <div className="ml-4 flex w-full justify-between items-start pb-0">
                                <div className="flex flex-col">
                                    <Typography.Text strong>
                                        {getStatusTitle(item.previousStatus, item.newStatus)}
                                    </Typography.Text>
                                </div>
                                <Typography.Text
                                    type="secondary"
                                    style={{ whiteSpace: "nowrap", marginLeft: 16 }}
                                    className="text-right"
                                >
                                    <DateField className="text-xs" value={item.createdAt} format="DD MMM YYYY" />
                                    <br />
                                    <DateField className="text-xs" value={item.createdAt} format="HH:mm" />
                                </Typography.Text>
                            </div>
                        ),
                    }))}
                />
            ) : (
                <div className="mt-8 text-center py-8">
                    <Typography.Text type="secondary">{_t("orders.history.noHistory")}</Typography.Text>
                </div>
            )}
        </GlassCard>
    );
};
